import Moment from 'moment';
import {complement, isEmpty, isNil, pickBy, pipe} from 'ramda';
import {set} from 'partial.lenses';
import queryString from 'query-string';
import {history as BHistory} from 'backbone';
import {
  getSortDirection,
  getSortColumn,
  getSelectedFilterNames,
  getSelectedFilterValue,
  getBroadcastFilter,
  setBroadcastFilter,
  unselectFilters,
  mergeFilter,
} from './selectors';

import {getFeatureFlag} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/featureFlags';

const myMediaFilterEnabled = () => getFeatureFlag('myMediaFilter');

export const getUrlParameter = (key, url = window.location.href) => {
  let urlQueryString;
  const queryIndex = url?.indexOf('?') ?? -1;

  if (queryIndex > -1) {
    urlQueryString = url.substring(queryIndex);
  }

  const value = queryString.parse(urlQueryString)[key];

  if (!value) {
    return [];
  }

  if (!(value instanceof Array)) {
    return [value];
  }

  return value;
};

const removeEmptyProperties = pickBy(complement(isNil));

export const _assignFiltersFromUrl = filterType => (defaultFilter, url) => {
  const selectedFilters = getUrlParameter(filterType, url);

  if (!selectedFilters.length) {
    return defaultFilter;
  }

  return defaultFilter ? {
    ...defaultFilter,
    filters: defaultFilter.filters.map(filter => ({
      ...filter,
      selected: selectedFilters.includes(filter.name) || selectedFilters.includes(filter.value?.[0]),
    })),
  } : undefined;
};

export const assignPackageTypeFiltersFromUrl = _assignFiltersFromUrl('packageType');
export const assignStatusFiltersFromUrl = _assignFiltersFromUrl('status');
export const assignFieldFiltersFromUrl = _assignFiltersFromUrl('field');
export const assignScopeOptionsFromUrl = _assignFiltersFromUrl('scope');
export const assignContentOptionsFromUrl = _assignFiltersFromUrl('content');
export const assignMyMediaOptionsFromUrl = _assignFiltersFromUrl('myMedia');
export const assignNetworkOptionsFromUrl = _assignFiltersFromUrl('network');
export const assignSourceOptionsFromUrl = _assignFiltersFromUrl('source');
export const assignTagOptionsFromUrl = _assignFiltersFromUrl('tag');
export const assignStatusOptionsFromUrl = _assignFiltersFromUrl('status');
export const assignDateRangeTypeOptionsFromUrl = _assignFiltersFromUrl('dateRangeType');

const assignSortFilterFromUrl = sortOptions => {
  const sortColumn = getUrlParameter('sortBy')[0];
  const sortDirection = getUrlParameter('sortOrder')[0];

  if (!sortOptions || !sortColumn || !sortDirection) {
    return sortOptions;
  }

  return {
    ...sortOptions,
    filters: sortOptions.filters.map(filter => ({
      ...filter,
      sortDirection,
      selected: filter.sortColumn === sortColumn,
    })),
  };
};

// todo deserialize search text filters
export const assignSearchTextFilterFromUrl = (defaultSearchTextFilter, url = window.location.href) => {
  const searchTerm = getUrlParameter('searchTerm', url)[0];

  return {
    ...defaultSearchTextFilter,
    text: searchTerm ? decodeURIComponent(searchTerm) : defaultSearchTextFilter.text,
  };
};

export const buildUrlQuery = parameters => {
  const parsed = queryString.parse(location.search);
  const augmented = Object.assign(parsed, parameters);
  const newURL = queryString.stringify(augmented);
  return newURL;
};

export const setUrlParameters = urlParams => {
  const queryIndex = BHistory.fragment.indexOf('?');
  let fragment = BHistory.fragment;
  if (queryIndex !== -1) {
    fragment = BHistory.fragment.substring(0, queryIndex);
  }

  if (isEmpty(urlParams || {})) {
    BHistory.navigate(fragment);
  } else if (queryIndex === -1) {
    const query = fragment + '?' + queryString.stringify(urlParams);
    BHistory.navigate(query, {replace: true});
  } else {
    const query = fragment + '?' + queryString.stringify(urlParams);
    BHistory.navigate(query);
  }
};

export const buildSearchQueryParameters = query => {
  const broadcastFilter = getBroadcastFilter(query);

  if (broadcastFilter && broadcastFilter.selected) {
    return {type: 'broadcast', channel: broadcastFilter.channel};
  }

  const {searchText, fromDate, thruDate, scope, content, myMedia, packageType, status, field, network, source, constraint, sortOrder, sortBy, dateRangeType, tag} = query;
  const querySortOrder = sortOrder || getSortDirection(query);
  const querySortBy = sortBy || getSortColumn(query);
  const urlParameters = {
    scope: scope ? getSelectedFilterNames(scope) : null,
    content: content ? getSelectedFilterNames(content) : null,
    packageType: packageType ? getSelectedFilterNames(packageType) : null,
    status: status ? getSelectedFilterNames(status) : null,
    constraint: constraint ? getSelectedFilterNames(constraint) : null,
    field: field ? getSelectedFilterNames(field) : null,
    network: network ? getSelectedFilterNames(network) : null,
    source: source ? getSelectedFilterNames(source) : null,
    tag: tag ? getSelectedFilterValue(tag) : null,
    // todo persist search text filters
    searchTerm: searchText?.text ? encodeURIComponent(searchText.text) : null,
    sortOrder: querySortOrder,
    sortBy: querySortBy,
    dateFrom: fromDate ? ((fromDate instanceof Date) ? fromDate.toISOString() : fromDate) : null,
    dateThru: thruDate ? ((thruDate instanceof Date) ? thruDate.toISOString() : thruDate) : null,
    dateRangeType: dateRangeType ? getSelectedFilterNames(dateRangeType) : null,
  };

  if (myMediaFilterEnabled()) {
    urlParameters.myMedia = myMedia ? getSelectedFilterNames(myMedia) : null;
  }

  return removeEmptyProperties(urlParameters);
};

// Testing this will cause an error
export const setUrlSearchQuery = pipe(buildSearchQueryParameters, setUrlParameters);

const buildBroadcastFilter = (defaultQuery, url) => {
  const packageTypeFilters = unselectFilters(defaultQuery.packageType.filters);
  const query = mergeFilter('packageType', packageTypeFilters, defaultQuery);
  const broadcastFilter = getBroadcastFilter(query);
  broadcastFilter.channel = getUrlParameter('channel', url)[0];
  broadcastFilter.selected = true;
  return setBroadcastFilter(broadcastFilter, query);
};

const setSearchEntityStatus = (filters, status) =>
  filters.map(filter => ({...filter, selected: filter.name === status}));

export const getUrlSearchQuery = ({defaultQuery}, url) => {
  const fromStr = getUrlParameter('dateFrom', url)[0];
  const thruStr = getUrlParameter('dateThru', url)[0];
  const searchMode = getUrlParameter('type', url)[0];
  const constraint = getUrlParameter('constraint', url)[0];
  if (searchMode === 'broadcast') {
    return buildBroadcastFilter(defaultQuery, url);
  }
  let query = {
    ...defaultQuery,
    searchText: assignSearchTextFilterFromUrl(defaultQuery.searchText),
    fromDate: fromStr ? Moment(decodeURIComponent(fromStr)).toDate() : defaultQuery.fromDate,
    thruDate: thruStr ? Moment(decodeURIComponent(thruStr)).toDate() : defaultQuery.thruDate,
    packageType: assignPackageTypeFiltersFromUrl(defaultQuery.packageType),
    field: assignFieldFiltersFromUrl(defaultQuery.field),
    sortOptions: assignSortFilterFromUrl(defaultQuery.sortOptions),
    network: assignNetworkOptionsFromUrl(defaultQuery.network),
    source: assignSourceOptionsFromUrl(defaultQuery.source),
    tag: assignTagOptionsFromUrl(defaultQuery.tag),
    status: assignStatusOptionsFromUrl(defaultQuery.status),
    dateRangeType: assignDateRangeTypeOptionsFromUrl(defaultQuery.dateRangeType),
  };

  if (constraint && defaultQuery.constraint) {
    const constraintQueryFilters = setSearchEntityStatus(defaultQuery.constraint.filters, constraint);
    query = set(['constraint', 'filters'], constraintQueryFilters)(query);
  }

  if (defaultQuery.scope) {
    query.scope = assignScopeOptionsFromUrl(defaultQuery.scope);
  }
  if (myMediaFilterEnabled() && defaultQuery.myMedia) {
    query.myMedia = assignMyMediaOptionsFromUrl(defaultQuery.myMedia);
  }

  return removeEmptyProperties(query);
};
